import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';

/**
 * Inline cell for the super-admin vendor list table — shows the
 * vendor's Content Studio credit balance + a "Set" button that opens
 * a small adjustment dialog. Each row owns its own fetch (one per
 * mount); after a Set succeeds the cell re-fetches so the displayed
 * number always matches the live ledger.
 */
export const VendorCreditsCell: React.FC<{ vendorId: string | undefined }> = ({ vendorId }) => {
    const { t } = useTranslation();
    const csApi = useContentStudioApi();

    const [balance, setBalance] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [targetInput, setTargetInput] = React.useState<string>('');
    const [reasonInput, setReasonInput] = React.useState<string>('');
    const [submitting, setSubmitting] = React.useState(false);
    const fieldId = React.useId();

    const refresh = React.useCallback(async () => {
        if (!vendorId) return;
        setLoading(true);
        setError(null);
        try {
            const r = await csApi.adminGetCreditsForVendor(vendorId);
            setBalance(r.balance);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('ADMIN_VENDORS.CREDITS.FAILED'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    React.useEffect(() => { refresh(); }, [refresh]);

    const submit = async () => {
        if (!vendorId) return;
        const target = Number(targetInput);
        if (!Number.isFinite(target) || target < 0) {
            setError(t('ADMIN_VENDORS.CREDITS.INVALID_TARGET'));
            return;
        }
        setSubmitting(true);
        try {
            const r = await csApi.adminSetCreditsForVendor(
                vendorId,
                Math.floor(target),
                reasonInput.trim() || undefined,
            );
            setBalance(r.newBalance);
            setDialogOpen(false);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('ADMIN_VENDORS.CREDITS.FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="flex items-center gap-2"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
            <span className="min-w-10 tabular-nums">
                {loading ? <Loader2 className="size-3 animate-spin" aria-label={t('ADMIN_SHELL.COMMON.LOADING')} /> : balance ?? '—'}
            </span>
            <Button
                variant="outline"
                size="xs"
                onClick={(e) => {
                    e.stopPropagation();
                    setTargetInput(balance != null ? String(balance) : '0');
                    setReasonInput('');
                    setDialogOpen(true);
                }}
                disabled={!vendorId}
            >
                {t('ADMIN_VENDORS.CREDITS.SET')}
            </Button>

            <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    if (!open && !submitting) setDialogOpen(false);
                }}
            >
                <DialogContent
                    className="sm:max-w-md"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <DialogHeader>
                        <DialogTitle className="font-heading text-lg font-medium tracking-tight">
                            {t('ADMIN_VENDORS.CREDITS.TITLE')}
                        </DialogTitle>
                    </DialogHeader>

                    <DialogDescription className="text-[13px] leading-relaxed">
                        {t('ADMIN_VENDORS.CREDITS.DESCRIPTION')}
                    </DialogDescription>

                    {error && <p className="text-xs text-destructive">{error}</p>}

                    <div>
                        <Label htmlFor={`${fieldId}-target`} className="mb-1.5 text-xs font-medium text-muted-foreground">{t('ADMIN_VENDORS.CREDITS.TARGET')}</Label>
                        <Input
                            id={`${fieldId}-target`}
                            type="number"
                            min={0}
                            step={1}
                            value={targetInput}
                            onChange={(e) => setTargetInput(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label htmlFor={`${fieldId}-reason`} className="mb-1.5 text-xs font-medium text-muted-foreground">{t('ADMIN_VENDORS.CREDITS.REASON')}</Label>
                        <Input
                            id={`${fieldId}-reason`}
                            value={reasonInput}
                            onChange={(e) => setReasonInput(e.target.value)}
                            placeholder={t('ADMIN_VENDORS.CREDITS.REASON_PLACEHOLDER')}
                        />
                        {balance != null && Number.isFinite(Number(targetInput)) && (
                            <p className="mt-1.5 text-[11px] text-muted-foreground">
                                {t('ADMIN_VENDORS.CREDITS.DELTA', { delta: Math.floor(Number(targetInput)) - balance })}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            disabled={submitting}
                        >
                            {t('ADMIN_SHELL.COMMON.CANCEL')}
                        </Button>
                        <Button onClick={submit} disabled={submitting}>
                            {submitting ? <Loader2 className="size-4 animate-spin" /> : t('ADMIN_SHELL.COMMON.SAVE')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
